import React, { useCallback, useState } from 'react';
import styled, { css } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { ContextualMenu, Icon, IconLookup } from 'component-library-and-tools';
import { useHistory } from 'react-router-dom';

import { openModal } from 'services/modal/actions';
import { REMOVE_WORKFLOW_MODAL } from 'shared/constants/modalNames';
import { EDIT_WORKFLOW_PATH, DARK_THEME } from 'shared/constants';
import { canUserManageWorkflows } from 'services/user/getters';
import { WorkflowStatus } from 'shared/types/workflow';
import { colors } from 'lib/prisma-styles';

import WorkflowStatusLabel from '../WorkflowStatusLabel';

import WorkflowItemDetail from '../WorkflowItemDetail';

import { RemoveWorkflowModal } from './RemoveWorkflowModal';
import { WorkflowItemProps } from './types';
import {
  DELETE_OPTION,
  EDIT_OPTION,
  DISABLED_WORKFLOW_CONTEXTUAL_MENU_OPTIONS,
  NON_DISABLED_WORKFLOW_CONTEXTUAL_MENU_OPTIONS,
} from './constants';
import { formatWorkflowTypeString } from 'shared/utils/api/mappers/workflowMapper';

const CONTEXTUAL_MENU_CLASS_NAME = 'workflow-item-contextual-menu';

const StyledOuterContainer = styled.div``;

const StyledContainer = styled.div`
  padding: 1.666666667rem 2rem;
  background-color: ${colors.background.section};
  border-radius: 8px;
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.12);
  font-size: 1.166666667rem;
`;

const StyledHeader = styled.div`
  display: flex;
  margin-bottom: 1rem;
  cursor: pointer;
`;

const StyledHeaderMainWrapper = styled.div`
  flex: 1;
  display: flex;
  margin-right: 1rem;
`;

const StyledIcon = styled(Icon)`
  // Minor adjustment of the icon.
  position: relative;
  top: 0.25rem;
`;

const StyledNameAndTypeWrapper = styled.div`
  margin-left: 1rem;
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  word-break: break-all;
`;

// TODO: Find a more meaningful name for this element!
const StyledRightBox = styled.div`
  display: inline-flex;
`;

const StyledName = styled.span`
  margin-bottom: 0.583333333rem;
  font-size: 1.333333333rem;
  font-weight: 700;
`;

const StyledType = styled.span``;

const StyledWorkflowStatusLabel = styled(WorkflowStatusLabel)`
  margin-right: 1.833333333rem;
`;

const StyledContextualMenuWrapper = styled.div``;

const StyledContextualMenu = styled(ContextualMenu)`
  && {
    .${CONTEXTUAL_MENU_CLASS_NAME} {
      background-color: ${colors.background.section};

      ${({ theme }) => theme.mode === DARK_THEME && css`
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.18), 0 2px 6px rgba(0, 0, 0, 0.36);
      `}
    }
  }
`;

const WorkflowItem = ({
  className,
  workflow,
}: WorkflowItemProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [startDeletingWorkflow, setStartDeletingWorkflowFlag] = useState(false);
  const dispatch = useDispatch();
  const canManageWorkflows = useSelector(canUserManageWorkflows);
  const history = useHistory();

  const handleHeaderClick = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const handleContextualMenuWrapperClick = useCallback((event) => {
    // To avoid opening and closing the item when clicking on the contextual menu, we stop
    // the propagation of the `click` event.
    event.stopPropagation();
  }, []);

  const handleContextualMenuOptionClick = useCallback((clickedOption) => {
    if (clickedOption === DELETE_OPTION.name) {
      setStartDeletingWorkflowFlag(true);
      dispatch(openModal(REMOVE_WORKFLOW_MODAL));
    } else if (clickedOption === EDIT_OPTION.name) {
      history.push(`${EDIT_WORKFLOW_PATH}/${workflow.guid}`);
    } // No `else` statement.
  }, [workflow]);

  return (
    <StyledOuterContainer data-cy={'workflow-row'} className={className}>
      <StyledContainer>
        <StyledHeader  data-cy={'workflow-row-header'} onClick={handleHeaderClick}>
          <StyledHeaderMainWrapper>
            <StyledIcon icon={isOpen ? IconLookup.CHEVRON_DOWN : IconLookup.CHEVRON_RIGHT} />
            <StyledNameAndTypeWrapper>
              <StyledName>{workflow.name}</StyledName>
              <StyledType>{formatWorkflowTypeString(workflow.type)}</StyledType>
            </StyledNameAndTypeWrapper>
          </StyledHeaderMainWrapper>
          <StyledRightBox>
            {workflow.status && <StyledWorkflowStatusLabel status={workflow.status} />}
            {canManageWorkflows && (
              <StyledContextualMenuWrapper data-cy={'workflow-row-actions'} onClick={handleContextualMenuWrapperClick}>
                <StyledContextualMenu
                  menuClassName={CONTEXTUAL_MENU_CLASS_NAME}
                  portalStyle={{ transform: 'translateX(-90%)' }}
                  onOptionClick={handleContextualMenuOptionClick}
                  options={workflow.status === WorkflowStatus.DISABLED
                    ? DISABLED_WORKFLOW_CONTEXTUAL_MENU_OPTIONS
                    : NON_DISABLED_WORKFLOW_CONTEXTUAL_MENU_OPTIONS
                  }
                />
              </StyledContextualMenuWrapper>
            )}
          </StyledRightBox>
        </StyledHeader>
        <WorkflowItemDetail
          isOpen={isOpen}
          workflow={workflow}
        />
      </StyledContainer>
      {startDeletingWorkflow && (
        <RemoveWorkflowModal
          workflow={workflow}
          onClose={() => setStartDeletingWorkflowFlag(false)}
        />
      )}
    </StyledOuterContainer>
  );
};

export default WorkflowItem;
